import { z } from "zod";

// Schema for rating skills (1-5 rating)
export const skillRatingSchema = z.object({
  skill_id: z.string(),
  skill_name: z.string(),
  rating: z.number().min(1).max(5),
});

// Base reference check fields
const baseReferenceCheckSchema = z.object({
  reference_id: z.string().uuid(),
  status: z.enum(['completed', 'denied', 'completed with comment', 'denied with comment'], {
    errorMap: () => ({ message: "Please select either 'Approve Reference' or 'Decline Reference'" }),
  }),
  comment: z.string().optional(),
});

// Work experience reference check
export const workExperienceReferenceCheckSchema = baseReferenceCheckSchema.extend({
  type: z.literal('work_experience'),
  job_title_confirmed: z.boolean(),
  job_title_actual: z.string().optional(), // If they want to correct it
  work_mode_confirmed: z.boolean(),
  work_mode_actual: z.string().optional(), // Changed from object to string
  job_description_confirmed: z.boolean(),
  job_description_actual: z.string().optional(), // If they want to correct it
  specialization_confirmed: z.boolean(),
  specialization_actual: z.string().optional(), // Changed from object to string
  start_date_confirmed: z.boolean(),
  start_date_actual: z.string().optional(), // If they want to correct it
  end_date_confirmed: z.boolean(),
  end_date_actual: z.string().optional(), // If they want to correct it
  currently_working: z.boolean().optional(), // For current employment status
  actual_end_date: z.string().optional(), // If they're no longer working there
  skills_ratings: z.array(skillRatingSchema),
  relationship_confirmed: z.boolean(),
  relationship_clarification: z.string().optional(), // If relationship is not confirmed
});

// Education reference check
export const educationReferenceCheckSchema = baseReferenceCheckSchema.extend({
  type: z.literal('education'),
  certification_title_confirmed: z.boolean(),
  certification_title_actual: z.string().optional(), // If they want to correct it
  certification_type_confirmed: z.boolean(),
  certification_type_actual: z.string().optional(), // Changed from object to string
  institution_name_confirmed: z.boolean(),
  institution_name_actual: z.string().optional(), // If they want to correct it
  start_date_confirmed: z.boolean(),
  start_date_actual: z.string().optional(), // If they want to correct it
  end_date_confirmed: z.boolean(),
  end_date_actual: z.string().optional(), // If they want to correct it
  skills_ratings: z.array(skillRatingSchema),
  relationship_confirmed: z.boolean(),
  relationship_clarification: z.string().optional(), // If relationship is not confirmed
});

// Union type for both reference check types
export const referenceCheckSchema = z.discriminatedUnion('type', [
  workExperienceReferenceCheckSchema,
  educationReferenceCheckSchema,
]);

export type SkillRating = z.infer<typeof skillRatingSchema>;
export type WorkExperienceReferenceCheck = z.infer<typeof workExperienceReferenceCheckSchema>;
export type EducationReferenceCheck = z.infer<typeof educationReferenceCheckSchema>;
export type ReferenceCheckType = z.infer<typeof referenceCheckSchema>;
